import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Heart, CreditCard, RefreshCcw, CheckCircle2, Loader2, Sprout, Server, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const WHY = [
  { icon: Server, text: "Servers, maps and email alerts that keep matches happening" },
  { icon: ShieldCheck, text: "Safeguarding, moderation and keeping the platform free of ads" },
  { icon: Sprout, text: "Free forever for volunteers, community groups and organisations" },
];

const FREQUENCY_OPTIONS = [{ id: "oneoff", label: "One-off" }, { id: "monthly", label: "Monthly" }];

export default function Donate() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState("tier_5");
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("oneoff");
  const [busy, setBusy] = useState(false);
  const [payState, setPayState] = useState(null); // checking | paid | failed

  useEffect(() => { api.donationsConfig().then(setConfig).catch(() => setConfig({ tiers: [], paypal_url: "" })); }, []);

  useEffect(() => {
    const sessionId = sp.get("session_id");
    if (!sessionId) return;
    setPayState("checking");
    let attempts = 0;
    const poll = async () => {
      if (attempts++ >= 6) return setPayState("failed");
      try {
        const s = await api.donationStatus(sessionId);
        if (s.payment_status === "paid") return setPayState("paid");
        if (s.status === "expired") return setPayState("failed");
        setTimeout(poll, 2000);
      } catch { setPayState("failed"); }
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional single-shot on mount

  const isOrg = user?.user_type === "organisation";
  const amount = custom ? Number(custom) : (config?.tiers?.find(t => t.id === selected)?.amount ?? 0);

  const donateCard = async () => {
    if (!amount || amount < 1) return toast.error("Pick an amount (£1 minimum)");
    setBusy(true);
    try {
      const body = custom ? { custom_amount: Number(custom) } : { tier_id: selected };
      const res = await api.donationCheckout({ ...body, origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  if (payState === "paid") {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="donate-thankyou">
        <div className="w-16 h-16 rounded-full bg-forest/10 flex items-center justify-center mx-auto">
          <Heart className="w-8 h-8 text-terracotta" />
        </div>
        <h1 className="font-display text-4xl text-forest mt-6">Thank you 💚</h1>
        <p className="text-neutral-600 mt-3">
          Your donation keeps CivicHearth free for volunteers, neighbours and organisations across the UK.
          You're part of the loop now.
        </p>
        <Button data-testid="donate-back-btn" onClick={() => { setSp({}); setPayState(null); }} variant="outline" className="rounded-full mt-8">
          Back to donations
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <Seo title="Support CivicHearth" description="CivicHearth is a non-profit — donate to keep volunteering free for everyone in the UK." />
      {payState === "checking" && (
        <div data-testid="donate-checking-banner" className="mb-6 flex items-center gap-2 bg-forest/5 border border-forest/10 rounded-xl px-5 py-3 text-sm text-forest">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming your donation…
        </div>
      )}
      {payState === "failed" && (
        <div data-testid="donate-failed-banner" className="mb-6 bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-sm text-red-700">
          We couldn't confirm the payment. If you were charged, it'll show up shortly — otherwise please try again.
        </div>
      )}

      <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold inline-flex items-center gap-1.5">
        <Heart className="w-4 h-4" /> Non-profit
      </div>
      <h1 className="font-display text-4xl sm:text-5xl text-forest mt-2 tracking-tight">Keep the loop going</h1>
      <p className="text-neutral-600 mt-3 max-w-2xl" data-testid="donate-pitch">
        {isOrg
          ? "CivicHearth is free for organisations of every kind — charities, community groups, schools and local businesses. If it's helped you find great volunteers, a donation keeps it that way for everyone."
          : "CivicHearth is run as a non-profit. Every hour matched, every neighbour helped and every badge earned is powered by donations from people like you."}
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {WHY.map((w) => (
          <div key={w.text} className="bg-white border border-neutral-200 rounded-xl p-4 flex items-start gap-3">
            <w.icon className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
            <span className="text-sm text-neutral-600">{w.text}</span>
          </div>
        ))}
      </div>

      <Card className="mt-8 border-neutral-200">
        <CardContent className="p-8">
          <div className="flex gap-2" data-testid="donate-frequency-toggle">
            {FREQUENCY_OPTIONS.map(f => (
              <button key={f.id} data-testid={`donate-freq-${f.id}`} onClick={() => setFrequency(f.id)}
                className={`text-sm px-4 py-2 rounded-full border transition-colors ${frequency === f.id ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700 hover:border-forest/40"}`}>
                {f.label}
              </button>
            ))}
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Choose an amount</label>
            <div className="flex flex-wrap gap-2 mt-3">
              {(config?.tiers || []).map(t => (
                <button key={t.id} data-testid={`donate-${t.id}`}
                  onClick={() => { setSelected(t.id); setCustom(""); }}
                  className={`text-base font-medium px-5 py-2.5 rounded-full border transition-colors ${selected === t.id && !custom ? "bg-terracotta text-white border-terracotta" : "bg-white border-neutral-200 text-neutral-700 hover:border-terracotta/40"}`}>
                  £{t.amount % 1 === 0 ? t.amount.toFixed(0) : t.amount}
                </button>
              ))}
              <div className="flex items-center gap-1.5">
                <span className="text-neutral-500 text-sm">or</span>
                <Input data-testid="donate-custom-input" type="number" min={1} max={1000} value={custom}
                  onChange={(e) => setCustom(e.target.value)} placeholder="Custom £" className="h-11 w-32" />
              </div>
            </div>
          </div>

          <div className="mt-8 space-y-3">
            {frequency === "oneoff" ? (
              <Button data-testid="donate-card-btn" onClick={donateCard} disabled={busy}
                className="w-full sm:w-auto rounded-full bg-forest hover:bg-forest-hover h-12 px-8 text-base">
                <CreditCard className="w-4 h-4 mr-2" />
                {busy ? "Opening secure checkout…" : `Donate £${amount || "—"} by card`}
              </Button>
            ) : (
              <div data-testid="donate-monthly-panel" className="bg-forest/5 border border-forest/10 rounded-xl p-5 text-sm text-neutral-700">
                <div className="font-medium text-forest inline-flex items-center gap-1.5"><RefreshCcw className="w-4 h-4 text-terracotta" /> Monthly giving</div>
                <p className="mt-1.5">Set up a monthly donation through PayPal — cancel any time from your PayPal account.</p>
              </div>
            )}

            {config?.paypal_url ? (
              <a data-testid="donate-paypal-btn" href={config.paypal_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center w-full sm:w-auto rounded-full h-12 px-8 text-base font-medium bg-[#FFC439] text-[#003087] hover:opacity-90 transition-opacity">
                Donate with PayPal
              </a>
            ) : (
              <div data-testid="donate-paypal-pending" className="text-xs text-neutral-500">
                PayPal donations {frequency === "monthly" ? "(including monthly giving) " : ""}are being set up and will appear here soon.
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-2 text-xs text-neutral-500">
            <CheckCircle2 className="w-3.5 h-3.5 text-forest/50" />
            Payments are processed securely by Stripe and PayPal — CivicHearth never sees your card details.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
